//—— 一卦 → 聊天串裡的兩則訊息 ——
//
//  從頁面抽出來獨立一支，有兩個理由：
//    1. 它是純資料轉換，沒有 JSX 也沒有狀態 —— 跟畫面混在一起時
//       要驗它就得先起一個瀏覽器，而這台機器上沒有。
//       抽出來就能拿資料庫裡真實的卦直接跑一遍。
//    2. 三條路徑都要用它（載入歷史、剛問完的那一卦、輪詢補上的斷語）。
//       各寫一份的話「歷史看到的」跟「剛問完看到的」會長得不一樣。

//—— 一卦 = 聊天串裡的一組問答 ——
//
//  不另外存一份訊息。問題在**扣券的當下**就寫進 Divination、斷語生成完也寫回
//  同一筆文件，聊天串本來就是那張表 —— 再存一份就是同一件事有兩個真相，
//  而兩邊遲早會分岔（CLAUDE.md）。
//
//  抽到模組層、而且只有這一份：載入歷史、剛問完的那一卦、輪詢補上的斷語，
//  三條路徑都走這裡。各寫一份的話「歷史看到的」跟「剛問完看到的」會長得不一樣。
export const PENDING_TEXT = "卦已成形，容我細看片刻。";

//—— 斷卦失敗時伏爻子說的話 ——
//
//  分成兩種，因為該講的完全不一樣：
//    還會再試  後端會自動補跑五次（見 utils/recovery-sweeper），
//              所以要說「等我」而不是「算了」—— 他什麼都不用做。
//    放棄了    補跑用完了，而**券不退**。這時唯一的出路是找客服，
//              所以要把卦號給他 —— 沒有編號的話客服查不到是哪一筆。
export const FAILED_TEXT =
  "此卦已成，但我一時看不真切。容我再斷一回，你稍後再來。";

export const givenUpText = (id) =>
  `此卦已成，我卻始終斷不分明，是老朽的不是。\n拿這個記號尋客服，他們會為你補上：${id}`;

export const hasVerdict = (d) => Boolean(d?.verdict?.terms && d?.verdict?.plain);

//—— 一卦擱多久就當它不會好了 ——
//
//  背景生成實測約 12 秒，輪詢本身也只撐 30 秒（15 次 × 2 秒）。
//  三分鐘還沒有斷語，代表那支背景任務在某個地方死掉了（程序被回收、
//  模型回了不合格的東西、例外被吞掉），再問一百次也不會變出來。
//
//  **這條界線是必要的，不是保險。** 沒有它的話，每次進頁面都會對所有
//  擱置的卦各輪詢 15 次 —— 開發期資料庫裡就躺著 18 卦這種（全部三小時以上），
//  一進頁面就是 270 支請求，而且一支都不會成功。
//
//  也順便蓋掉另一種壞掉的形狀：status 是 done 但 verdict 不是三段的
//  （schema 換過之前存的 { text } ）。那種 hasVerdict 是 false，
//  沒有時效界線的話會被當成「還在跑」而永遠輪詢下去。
export const VERDICT_STALE_MS = 3 * 60 * 1000;

export const isPending = (d) =>
  !hasVerdict(d) &&
  !d.givenUp &&
  d.status !== "failed" &&
  Date.now() - new Date(d.castAt || d.createdAt).getTime() < VERDICT_STALE_MS;

//斷語的內容。
//
//  卦象那一塊**不經過 AI** —— 卦名、陰陽、動爻、世應都是排盤器算出來的事實，
//  交給模型描述一次等於給它一個寫錯的機會，而那是整則訊息裡最不該錯的地方。
export const verdictBlocks = (d) => [
  { type: "hexagram", data: { gua: d.gua } },
  //  卦象剖析預設折起來（Kevin 2026-10-08）。
  //  那一段是寫給看得懂術語的人的，用神、生剋、爻辭對一般使用者是一道牆 ——
  //  先讓他看到白話解析的結論，想追究再自己打開。
  //  折疊與否是內容的性質，所以標在資料上而不是版型裡。
  {
    type: "section",
    data: { title: "卦象剖析", text: d.verdict.terms, collapsed: true },
  },
  { type: "section", data: { title: "白話解析", text: d.verdict.plain } },
  //  建議是後來才加的第三段（Kevin 2026-10-08）。在那之前存下來的斷語
  //  只有兩段，所以有才畫 —— 硬畫會變成一個空標題。
  ...(d.verdict.advice
    ? [{ type: "section", data: { title: "給你的建議", text: d.verdict.advice } }]
    : []),
];

//伏爻子那一則。三種狀態各一種樣子。
//
//  還在生成時給的 id 是 `pending-<卦號>` —— 輪詢拿到斷語之後就是靠它
//  找到該換掉哪一則（見 fillVerdict）。
export const masterMessage = (d) => {
  const id = d.divinationID;

  if (hasVerdict(d)) {
    return { id, role: "master", blocks: verdictBlocks(d) };
  }

  //  還在跑（cast / reading，而且還沒擱太久）
  if (isPending(d)) {
    return { id: `pending-${id}`, role: "master", text: PENDING_TEXT };
  }

  //  到這裡就是沒有斷語、而且不會再有了：failed，或擱置過久。
  //
  //  券已經扣了、卦也定了，失敗的是生成那一步。
  //  講清楚卦還在，不要讓他以為整件事沒了。
  //  卦象圖照畫 —— 那一塊不依賴 AI，他至少看得到自己搖出什麼。
  return {
    id,
    role: "master",
    //  放棄了才給卦號與客服，還會再試的只要請他等（見上面那兩段文案）
    text: d.givenUp ? givenUpText(id) : FAILED_TEXT,
    blocks: d.gua ? [{ type: "hexagram", data: { gua: d.gua } }] : [],
  };
};

export const divinationMessages = (d) => [
  { id: `q-${d.divinationID}`, role: "user", text: d.question },
  masterMessage(d),
];
